import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { TextField } from "./TextField";
import {
  ComponentType,
  TextFieldComponent,
  ValidationRule,
  WidthMode,
} from "../../services/types/ComponentTypes";
import {
  CompanyBankAccount,
  CompanyBankAccountPayload,
  createCompanyBankAccount,
  getCompanyBankAccount,
  updateCompanyBankAccount,
} from "../../api/companyBankAccounts";
import { resolveLoginUser } from "../../services/api";

export type BankAccountModalMode = "add" | "edit";

interface BankAccountModalProps {
  isOpen: boolean;
  mode: BankAccountModalMode;
  recordId?: string;
  onClose: () => void;
  onSuccess: (saved: CompanyBankAccount) => void;
}

const emptyForm: CompanyBankAccountPayload = {
  account_title: "",
  iban: "",
  account_number: "",
  bic_swift: "",
  bank: "",
  branch_code: "",
  branch_name: "",
};

function fieldConfig(
  name: keyof CompanyBankAccountPayload,
  label: string,
  required = false
): TextFieldComponent {
  return {
    componentType: ComponentType.TEXT_FIELD,
    name,
    label,
    required,
    validation: required
      ? [{ rule: ValidationRule.REQUIRED, message: `${label} is required` }]
      : undefined,
    style: { widthMode: WidthMode.FULL },
  };
}

const FIELD_CONFIGS: {
  name: keyof CompanyBankAccountPayload;
  label: string;
  required?: boolean;
}[] = [
  { name: "account_title", label: "Account Title", required: true },
  { name: "iban", label: "IBAN" },
  { name: "account_number", label: "Account Number" },
  { name: "bic_swift", label: "BIC / SWIFT" },
  { name: "bank", label: "Bank" },
  { name: "branch_code", label: "Branch Code" },
  { name: "branch_name", label: "Branch Name" },
];

export const BankAccountModal: React.FC<BankAccountModalProps> = ({
  isOpen,
  mode,
  recordId,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<CompanyBankAccountPayload>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const load = async () => {
      setError("");
      if (mode === "edit" && recordId) {
        setLoading(true);
        try {
          const account = await getCompanyBankAccount(recordId);
          if (cancelled) return;
          setFormData({
            account_title: account.account_title || "",
            iban: account.iban || "",
            account_number: account.account_number || "",
            bic_swift: account.bic_swift || "",
            bank: account.bank || "",
            branch_code: account.branch_code || "",
            branch_name: account.branch_name || "",
          });
        } catch (e) {
          if (!cancelled) {
            setError(e instanceof Error ? e.message : "Failed to load bank account.");
            setFormData(emptyForm);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      } else {
        setFormData(emptyForm);
        setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, recordId]);

  if (!isOpen) return null;

  const handleChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const title = (formData.account_title || "").trim();
    if (!title) {
      setError("Account Title is required.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload: CompanyBankAccountPayload = {
        account_title: title,
        iban: (formData.iban || "").trim(),
        account_number: (formData.account_number || "").trim(),
        bic_swift: (formData.bic_swift || "").trim(),
        bank: (formData.bank || "").trim(),
        branch_code: (formData.branch_code || "").trim(),
        branch_name: (formData.branch_name || "").trim(),
      };

      const isCreate = !(mode === "edit" && recordId);
      // createCompanyBankAccount also injects branch; set here so Network payload shows it.
      if (isCreate) {
        const loginUser = resolveLoginUser() as Record<string, unknown> | undefined;
        const branchId = String(
          loginUser?.branch_id ??
            loginUser?.branchId ??
            loginUser?.transaction_branch_id ??
            loginUser?.transactionBranchId ??
            ""
        ).trim();
        if (branchId) {
          payload.branch_id = branchId;
          payload.transaction_branch_id = branchId;
        } else {
          console.warn(
            "[BankAccountModal] Login branch_id missing; create API will retry resolveLoginUser."
          );
        }
      }

      const saved = isCreate
        ? await createCompanyBankAccount(payload)
        : await updateCompanyBankAccount(recordId!, payload);

      onSuccess(saved);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save bank account.");
    } finally {
      setSaving(false);
    }
  };

  const title = mode === "edit" ? "Edit bank account" : "Add bank account";

  return createPortal(
    <div className="fixed inset-0 z-[1500] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 m-0">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 rounded-lg p-1 transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar relative">
          {error && (
            <div className="mb-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded dark:bg-red-900/30 dark:border-red-800 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500 dark:text-gray-400">
              Loading…
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
              {FIELD_CONFIGS.map(({ name, label, required }) => (
                <div
                  key={name}
                  className={name === "account_title" || name === "bank" ? "md:col-span-2" : undefined}
                >
                  <TextField
                    config={fieldConfig(name, label, required)}
                    value={formData[name] || ""}
                    onChange={handleChange}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || loading}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 hover:bg-primary-600 text-white transition-colors disabled:opacity-50"
          >
            {saving ? "Saving…" : "Submit"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
